import { useCallback, useRef } from 'react';

import { type DocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Grid, useTheme } from '@mui/material';
import { useFileDialog } from '@realadvisor/hooks';
import { Image } from '@realadvisor/image';

import type {
  DeletePropertyImageMutationVariables,
  StepCoverPhotoBrochureFragment,
} from '@graphql/__generated__/graphql';
import { FullScreenDropZone } from '@modules/_shared/components/DropZone';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { UploadImageCard } from '@modules/_shared/components/UploadCard';
import { useFileUpload } from '@modules/_shared/utils/files/file-upload';
import {
  DELETE_PROPERTY_IMAGE,
  INSERT_PROPERTY_IMAGE,
} from '@modules/listings/ListingPropertyImages/listingPropertyQueries';
import { updatePropertyImagesCountCache } from '@modules/listings/ListingPropertyImages/propertyImageCache';
import LoadingCard from '@modules/reports/shared/CoverPhotoSelector/LoadingCard';
import PropertyImageItem from '@modules/reports/shared/CoverPhotoSelector/PropertyImageItem';
import SelectableCard from '@modules/reports/shared/CoverPhotoSelector/SelectableCard';
import { useLocale } from '@shared/hooks/locale';
import { useMap } from '@shared/hooks/useMap';

export type StepCoverPropertyImageType = {
  id: string;
  image: {
    id: string;
    url: string;
  };
};

type CoverPhotoSelectorProps = {
  propertyImages: StepCoverPropertyImageType[];
  coverImageId: string | undefined;
  propertyId: string;
  type: 'lots' | 'cma_reports';
  onUpdate: (newCoverImageId: string | null) => void;
  fragment: DocumentNode;
  fragmentName: string;
  satelliteImageUrl?: string;
};

const CoverPhotoSelector = ({
  propertyImages,
  coverImageId,
  propertyId,
  type,
  onUpdate,
  fragment,
  fragmentName,
  satelliteImageUrl,
}: CoverPhotoSelectorProps) => {
  const lastId = useRef(0);
  const [uploadImage] = useFileUpload('image');
  const { data } = useReportContext();
  const localImages = useMap<string>();
  const theme = useTheme();
  const { t } = useLocale();

  const [insertPropertyImage] = useMutation(INSERT_PROPERTY_IMAGE, {
    update: (cache, { data: newData }) => {
      if (newData?.insert_property_images_one) {
        updatePropertyImagesCountCache(cache, propertyId, 1);

        const id = cache.identify({
          __typename: type,
          id: data?.id,
        });
        const existingData = cache.readFragment<StepCoverPhotoBrochureFragment>(
          {
            id,
            fragment,
            fragmentName,
          },
        );

        if (!existingData) {
          return;
        }

        cache.writeFragment({
          fragment,
          fragmentName,
          id,
          data: {
            ...existingData,
            cover_image: {
              id: newData.insert_property_images_one?.image?.id ?? '',
              url: newData.insert_property_images_one?.image?.url ?? '',
            },
            property: {
              ...existingData?.property,
              property_images: [
                newData.insert_property_images_one,
                ...(existingData?.property?.property_images ?? []),
              ],
            },
          },
        });

        onUpdate(newData.insert_property_images_one.image.id ?? null);
      }
    },
  });

  const handleDrop = useCallback(
    (files: File[]) => {
      files.forEach(image => {
        const index = lastId.current;
        lastId.current += 1;
        const reader = new FileReader();

        reader.onload = evt => {
          localImages.set(`${index}`, evt.target?.result as string);
        };

        reader.readAsDataURL(image);

        uploadImage(image, ({ data: imageData, error }) => {
          if (error) {
            console.error(error);
            return;
          }

          insertPropertyImage({
            variables: {
              object: {
                property_id: propertyId,
                image: {
                  data: {
                    url: imageData?.url,
                  },
                },
                display_on_brochure: true,
                display_on_listing: false,
              },
            },
            onCompleted: () => {
              localImages.delete(`${index}`);
            },
          });
        });
      });
    },
    [propertyId, insertPropertyImage, localImages, uploadImage],
  );

  const openFileDialog = useFileDialog({
    accept: 'image/*',
    onChange: handleDrop,
  });

  const [deletePropertyImage] = useMutation(DELETE_PROPERTY_IMAGE, {
    update: (cache, { data: deletedData }, { variables }) => {
      const { id } = variables as DeletePropertyImageMutationVariables;

      if (!deletedData?.delete_property_images_by_pk) {
        return;
      }

      cache.evict({ id: `property_images:${id}` });
      updatePropertyImagesCountCache(cache, propertyId, -1);
    },
  });

  const handleDeleteImage = useCallback(
    (imageId: string) => {
      deletePropertyImage({
        variables: { id: imageId },
        onCompleted: () => {
          const newPropertyImages = propertyImages.filter(
            img => img.id !== imageId,
          );

          // Update cover image if the deleted image was the cover image
          const deletedImage = propertyImages.find(img => img.id === imageId);
          if (deletedImage?.image.id === coverImageId) {
            onUpdate(newPropertyImages?.[0]?.image.id ?? null);
          }
        },
      });
    },
    [coverImageId, propertyImages, deletePropertyImage, onUpdate],
  );

  return (
    <>
      <FullScreenDropZone onDrop={handleDrop} />
      <Box
        sx={{
          p: 2,
          mb: 1,
          mx: -2,
        }}
      >
        <UploadImageCard noMinHeight onClick={openFileDialog} />
      </Box>
      <Grid
        container
        sx={{
          flexDirection: 'column',
          gap: 2,
          paddingBottom: 1,
        }}
      >
        {satelliteImageUrl && (
          <SelectableCard
            selected={coverImageId == null}
            onChange={selected => {
              if (selected) {
                onUpdate(null);
              }
            }}
          >
            <Image
              src={satelliteImageUrl}
              objectFit="cover"
              options={{ w: 450, h: 200, c: 'scale' }}
            />
            <div
              style={{
                ...theme.typography.caption,
                position: 'absolute',
                left: 10,
                bottom: 10,
                display: 'inline-flex',
                borderRadius: 99,
                color: '#fff',
                backgroundColor: (theme.vars ?? theme).palette.primary.main,
                padding: '0 8px',
                margin: 2,
              }}
            >
              {t('satellite')}
            </div>
          </SelectableCard>
        )}
        {Array.from(localImages.entries()).map(([index, image]) => (
          <LoadingCard loading key={`cover-photo-${index}`}>
            <img
              src={image}
              css={{ objectFit: 'cover', width: '100%', height: 200 }}
              alt={`cover-photo-${index}`}
            />
          </LoadingCard>
        ))}

        {propertyImages.map(pi => (
          <PropertyImageItem
            key={pi.id}
            propertyImage={pi}
            isSelected={pi.image.id === coverImageId}
            onSelect={selected => {
              onUpdate(selected ? pi.image.id : null);
            }}
            onDelete={handleDeleteImage}
          />
        ))}
      </Grid>
    </>
  );
};

export default CoverPhotoSelector;
